/**
 * Thin wrapper around the Minuta auth API (Go + Cognito on AWS API Gateway).
 *
 * Base URL overridable via `VITE_AUTH_API_URL` at build time.
 *
 * CORS note: API Gateway needs to allow requests from the landing's origin
 * (e.g. https://minuta.app). That's configured at the gateway level, not in
 * the Go code. If requests fail in the browser with a CORS error, that's
 * where to fix it.
 */

const DEFAULT_BASE =
  "https://xj2dj79orh.execute-api.us-east-1.amazonaws.com";

const BASE_URL =
  (import.meta.env.VITE_AUTH_API_URL as string | undefined)?.replace(/\/$/, "") ||
  DEFAULT_BASE;

export interface SignupInput {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  /** reCAPTCHA v3 token for action="signup". Empty → backend skips check. */
  recaptchaToken?: string;
  source?: string;
  channel?: string;
  campaign?: string;
}

export interface ConfirmInput {
  email: string;
  code: string;
  /** reCAPTCHA v3 token for action="confirm_signup". */
  recaptchaToken?: string;
}

/**
 * Errors returned by the API follow this shape (see api/internal/httpx/httpx.go).
 * We expose `code` so callers can render specific messages for known cases
 * like `user_exists` / `code_mismatch` / `code_expired` / `rate_limited`.
 */
export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(
  method: "GET" | "POST",
  path: string,
  body?: Record<string, unknown>,
  accessToken?: string,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "Network error — verifique sua conexão.", "network");
  }

  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      /* non-JSON body — fall through with null */
    }
  }

  if (!res.ok) {
    const obj = (json ?? {}) as { error?: string; message?: string; code?: string };
    throw new ApiError(
      res.status,
      obj.message ?? obj.error ?? `Request failed (${res.status})`,
      obj.code,
    );
  }

  return (json as T) ?? ({} as T);
}

function post<T>(path: string, body: Record<string, unknown>, accessToken?: string): Promise<T> {
  return request<T>("POST", path, body, accessToken);
}

function get<T>(path: string, accessToken?: string): Promise<T> {
  return request<T>("GET", path, undefined, accessToken);
}

export function signup(input: SignupInput) {
  return post<{ status: "pending_confirmation"; message?: string }>(
    "/auth/signup",
    {
      email: input.email.trim().toLowerCase(),
      password: input.password,
      first_name: input.firstName?.trim() || undefined,
      last_name: input.lastName?.trim() || undefined,
      recaptcha_token: input.recaptchaToken || undefined,
      source: input.source?.trim() || undefined,
      channel: input.channel?.trim() || undefined,
      campaign: input.campaign?.trim() || undefined,
    },
  );
}

export function confirm(input: ConfirmInput) {
  return post<{ status: "confirmed" }>("/auth/confirm", {
    email: input.email.trim().toLowerCase(),
    code: input.code.trim(),
    recaptcha_token: input.recaptchaToken || undefined,
  });
}

export interface LeadMagnetInput {
  email: string;
  locale?: string;
  source?: string;
  leadMagnet?: string;
  recaptchaToken?: string;
  /** Hidden honeypot field. Non-empty values are silently ignored server-side. */
  website?: string;
  referrer?: string;
}

export function captureLeadMagnet(input: LeadMagnetInput) {
  return post<{ status: "accepted" }>("/marketing/lead-magnet", {
    email: input.email.trim().toLowerCase(),
    locale: input.locale?.trim() || undefined,
    source: input.source?.trim() || undefined,
    lead_magnet: input.leadMagnet?.trim() || undefined,
    recaptcha_token: input.recaptchaToken || undefined,
    website: input.website?.trim() || undefined,
    referrer: input.referrer?.trim() || undefined,
  });
}

export interface Tokens {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

export function login(email: string, password: string, recaptchaToken?: string) {
  return post<Tokens>("/auth/login", {
    email: email.trim().toLowerCase(),
    password,
    recaptcha_token: recaptchaToken || undefined,
  });
}

/**
 * Trade a Cognito Hosted UI authorization code (returned to /auth/callback)
 * for a TokenSet. Same shape as `login`. The PKCE `code_verifier` must match
 * the `code_challenge` we sent on /oauth2/authorize.
 */
export function exchangeOAuthCode(code: string, redirectUri: string, codeVerifier: string) {
  return post<Tokens>("/auth/oauth/exchange", {
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  });
}

/**
 * Minuta ships separate macOS builds for Apple Silicon and Intel, plus
 * Windows builds for x64 and ARM64. The API exposes seven reserved aliases
 * resolved server-side from the releases bucket:
 *
 *   - `latest`                     → most recent Minuta-*.dmg, any arch (legacy
 *                                    fallback; non-deterministic)
 *   - `latest-arm64`               → most recent Apple Silicon DMG
 *   - `latest-x64`                 → most recent Intel Mac DMG
 *   - `latest-win-x64-setup`      → most recent Windows x64 NSIS installer
 *   - `latest-win-x64-portable`   → most recent Windows x64 portable .exe
 *   - `latest-win-arm64-setup`    → most recent Windows on ARM NSIS installer
 *   - `latest-win-arm64-portable` → most recent Windows on ARM portable .exe
 *
 * Prefer the scoped aliases in new callers. `latest` only survives so old
 * bookmarks don't 404.
 */
export type MacArch = "arm64" | "x64";

export type PlatformBuild =
  | "mac-arm64"
  | "mac-x64"
  | "win-x64-setup"
  | "win-x64-portable"
  | "win-arm64-setup"
  | "win-arm64-portable";

export const RELEASE_FILENAME = "latest";
export const RELEASE_FILENAME_ARM64 = "latest-arm64";
export const RELEASE_FILENAME_X64 = "latest-x64";
export const RELEASE_FILENAME_WIN_X64_SETUP = "latest-win-x64-setup";
export const RELEASE_FILENAME_WIN_X64_PORTABLE = "latest-win-x64-portable";
export const RELEASE_FILENAME_WIN_ARM64_SETUP = "latest-win-arm64-setup";
export const RELEASE_FILENAME_WIN_ARM64_PORTABLE = "latest-win-arm64-portable";

const BUILD_ALIAS: Record<PlatformBuild, string> = {
  "mac-arm64": RELEASE_FILENAME_ARM64,
  "mac-x64": RELEASE_FILENAME_X64,
  "win-x64-setup": RELEASE_FILENAME_WIN_X64_SETUP,
  "win-x64-portable": RELEASE_FILENAME_WIN_X64_PORTABLE,
  "win-arm64-setup": RELEASE_FILENAME_WIN_ARM64_SETUP,
  "win-arm64-portable": RELEASE_FILENAME_WIN_ARM64_PORTABLE,
};

/**
 * Absolute URL the browser should navigate to for a release artifact. The API
 * responds with a 302 to a short-lived S3 presigned URL whose
 * `Content-Disposition: attachment` header makes the browser download the file
 * with its original name — no bytes pass through the Lambda.
 *
 * Legacy signature: pass a `MacArch` for the Mac builds. Omit the argument to
 * hit the backwards-compat `latest` alias. For Windows builds use
 * `downloadUrlForBuild("win-x64-setup" | "win-x64-portable")` instead.
 */
export function downloadUrl(arch?: MacArch): string {
  const filename = arch === "arm64"
    ? RELEASE_FILENAME_ARM64
    : arch === "x64"
      ? RELEASE_FILENAME_X64
      : RELEASE_FILENAME;
  return `${BASE_URL}/downloads/${encodeURIComponent(filename)}`;
}

/**
 * Download URL for any platform build — Mac arm64/x64 or Windows setup/portable.
 * Prefer this in new UI; `downloadUrl` is kept for the legacy `latest` fallback
 * and existing Mac-only call sites.
 */
export function downloadUrlForBuild(build: PlatformBuild): string {
  return `${BASE_URL}/downloads/${encodeURIComponent(BUILD_ALIAS[build])}`;
}

/**
 * Best-effort client-side Mac architecture detection.
 *
 * `userAgentData.getHighEntropyValues(['architecture'])` is the only API that
 * reliably distinguishes Apple Silicon from Intel in the browser — and it's
 * Chromium-only (Chrome, Edge, Arc, Brave). Safari and Firefox both lie about
 * the arch in their User-Agent string for compat (they still advertise
 * "Intel Mac OS X" even on M-series hardware). Outside Chromium we return
 * `null` and leave the caller to pick a default.
 *
 * Result:
 *   "arm64"  → Apple Silicon (M1/M2/M3/M4)
 *   "x64"    → Intel
 *   null     → unable to determine
 */
export async function detectMacArch(): Promise<MacArch | null> {
  interface UAData {
    getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string }>;
  }
  const uaData = (navigator as unknown as { userAgentData?: UAData }).userAgentData;
  if (!uaData?.getHighEntropyValues) return null;
  try {
    const result = await uaData.getHighEntropyValues(["architecture"]);
    if (result.architecture === "arm") return "arm64";
    if (result.architecture === "x86") return "x64";
  } catch {
    /* High-entropy hints can be refused (embargoed iframe, permission
       policy, etc.) — treat the same as "unknown". */
  }
  return null;
}

/**
 * Coarse OS detection from the user-agent. Enough to steer the /downloads
 * page toward the right set of buttons (Mac arm64/x64 vs Windows). Returns
 * `null` when we can't tell.
 *
 *   "mac"      → macOS (all Mac flavours — pair with detectMacArch for arch)
 *   "windows"  → Windows (any arch; we only ship x64 today)
 *   "linux"    → something Linux-like (not yet supported)
 *   null       → unknown
 */
export type DetectedOS = "mac" | "windows" | "linux";

export function detectOS(): DetectedOS | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  // Check platform hints first when available — more reliable than the UA.
  const platform = (navigator as Navigator & { userAgentData?: { platform?: string } })
    .userAgentData?.platform?.toLowerCase();
  if (platform) {
    if (platform.includes("mac")) return "mac";
    if (platform.includes("windows")) return "windows";
    if (platform.includes("linux")) return "linux";
  }
  // iOS advertises "iPhone"/"iPad"/"iPod" (plus the word "Mac" on iPadOS with
  // "Request Desktop Site"). We don't ship an iOS build, so return `null`
  // rather than routing iOS users to the Mac section — neither platform
  // picker is correct for them.
  if (/iPhone|iPad|iPod/i.test(ua)) return null;
  if (/Mac/i.test(ua)) return "mac";
  if (/Windows/i.test(ua)) return "windows";
  if (/Linux|X11/i.test(ua)) return "linux";
  return null;
}

export function logout(refreshToken: string) {
  return post<{ status: "revoked" }>("/auth/logout", {
    refresh_token: refreshToken,
  });
}

/**
 * Exchange a refresh token for a fresh access + id token set. Called on
 * /billing/done so the new `pro_users` group claim lands in the id_token
 * without the user having to log out and back in.
 *
 * Note: Cognito does not rotate the refresh_token — the returned Tokens may
 * omit it; callers should keep the existing refresh token when the response
 * has an empty `refresh_token`.
 */
export function refresh(refreshToken: string) {
  return post<Tokens>("/auth/refresh", { refresh_token: refreshToken });
}

// ---------------------------------------------------------------------------
//  Billing — all auth-required except the webhook (which Stripe hits directly
//  and is never called from the browser).
// ---------------------------------------------------------------------------

export type Plan = "free" | "pro";
export type CheckoutCycle = "monthly" | "annual" | "founding100";

export type SubscriptionStatus =
  | "active"
  | "trialing"
  | "past_due"
  | "canceled"
  | "incomplete";

export interface Entitlement {
  plan: Plan;
  status?: SubscriptionStatus;
  /** Unix seconds. 0/absent when there's no active subscription. */
  current_period_end?: number;
  /** Unix seconds. Set while Stripe is retrying a failed invoice. */
  grace_until?: number;
  /** True once the user has at least one Stripe Customer on record. */
  portal_available: boolean;
}

/** POST /billing/checkout → { url }. Redirect the browser to the returned URL.
 *
 * Pass `opts.cycle` to select the billing period:
 *   "monthly"     — billed each month (default when omitted)
 *   "annual"      — billed once per year
 *   "founding100" — launch offer, capped server-side
 *
 * Omitting opts (or opts.cycle) sends an empty body, which keeps the existing
 * backend default of monthly billing.
 */
export function createCheckoutSession(
  accessToken: string,
  opts?: {
    cycle?: CheckoutCycle;
    source?: string;
    channel?: string;
    campaign?: string;
    content?: string;
  },
) {
  const body: Record<string, unknown> = {};
  if (opts?.cycle) body["cycle"] = opts.cycle;
  if (opts?.source) body["source"] = opts.source.trim();
  if (opts?.channel) body["channel"] = opts.channel.trim();
  if (opts?.campaign) body["campaign"] = opts.campaign.trim();
  if (opts?.content) body["content"] = opts.content.trim();
  return post<{ url: string }>("/billing/checkout", body, accessToken);
}

export function getFounding100Remaining() {
  return get<{ remaining: number; cap: number; cached_at: string }>("/api/founding-100/remaining");
}

export type LaunchFunnelEvent =
  | "launch_visit"
  | "launch_signup"
  | "launch_activation"
  | "launch_paid"
  | "desktop_download_clicked";

export interface LaunchFunnelInput {
  event: LaunchFunnelEvent;
  distinctId: string;
  source?: string;
  channel?: string;
  campaign?: string;
  content?: string;
  app?: string;
  path?: string;
  action?: string;
}

export function emitLaunchFunnelEvent(input: LaunchFunnelInput) {
  return post<{ status: "accepted" }>("/analytics/funnel", {
    event: input.event,
    distinct_id: input.distinctId.trim(),
    source: input.source?.trim() || undefined,
    channel: input.channel?.trim() || undefined,
    campaign: input.campaign?.trim() || undefined,
    content: input.content?.trim() || undefined,
    app: input.app?.trim() || undefined,
    path: input.path?.trim() || undefined,
    action: input.action?.trim() || undefined,
  });
}

/** POST /billing/portal → { url }. Short-lived Stripe customer-portal URL. */
export function createPortalSession(accessToken: string) {
  return post<{ url: string }>("/billing/portal", {}, accessToken);
}

/** GET /users/me/entitlement — what plan is this user on right now? */
export function getEntitlement(accessToken: string) {
  return get<Entitlement>("/users/me/entitlement", accessToken);
}

/**
 * Friendly error text for the common Cognito-backed error codes. Falls back
 * to the raw API message if the code is unknown.
 */
export function formatError(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return "Algo deu errado. Tente novamente em instantes.";
  }
  switch (err.code) {
    case "user_exists":
      return "Este email já está cadastrado. Tente entrar no app.";
    case "code_mismatch":
      return "Código incorreto. Verifique o email e tente de novo.";
    case "code_expired":
      return "Código expirado. Peça um novo cadastro para gerar outro.";
    case "rate_limited":
      return "Muitas tentativas. Aguarde alguns minutos e tente de novo.";
    case "invalid_credentials":
      return "Email ou senha incorretos.";
    case "user_not_confirmed":
      return "Confirme seu email antes de entrar. Verifique sua caixa de entrada.";
    case "captcha_required":
    case "captcha_failed":
      return "Verificação anti-bot falhou. Recarregue a página e tente de novo.";
    case "captcha_unreachable":
      return "Não conseguimos acessar o serviço anti-bot. Tente de novo em instantes.";
    case "network":
      return err.message;
    default:
      return err.message || "Algo deu errado. Tente novamente em instantes.";
  }
}
